// ---------------------------------------------------------------------------
// banners-overview-card — headline counts (active / archived / expiring-soon)
// ---------------------------------------------------------------------------
//
// Reads GET /banners/cards-stats (computed server-side from live banners rows)
// and renders three figures. Exposes `refresh` so the crud-page refreshAll()
// updates it after a save/archive/delete.

import { createElement } from '../../core/dom/helpers.js';
import { numberFormat } from '../../core/charts/svg-util.js';
import { isAbortError } from '../../core/http/is-abort-error.js';

/**
 * @typedef {import('../../contracts/card-grid.js').CardDescriptor} CardDescriptor
 * @typedef {import('../../contracts/card-grid.js').CardHandle} CardHandle
 * @typedef {import('../factories/crud-page.js').CardsSectionContext} Ctx
 * @typedef {CardDescriptor & { render: (ctx: Ctx) => CardHandle }} BannersCard
 */

/** @returns {BannersCard} */
export function createBannersOverviewCard() {
  return {
    id: 'banners.overview',
    title: 'Banners overview',
    render(ctx) {
      return mountOverview(ctx);
    },
  };
}

/** @param {Ctx} ctx @returns {CardHandle} */
function mountOverview(ctx) {
  const el = createElement('div', { class: 'banners-card' });
  let destroyed = false;
  let seq = 0;

  /** @param {string} text */
  function renderMessage(text) {
    el.replaceChildren(createElement('p', { class: 'banners-card__message' }, [text]));
  }

  /** @param {{ active?: number, archived?: number, expiringSoon?: number }} stats */
  function render(stats) {
    const figures = [
      { label: 'Active', value: Number(stats.active) || 0 },
      { label: 'Archived', value: Number(stats.archived) || 0 },
      { label: 'Expiring soon', value: Number(stats.expiringSoon) || 0 },
    ].map((f) => createElement('div', { class: 'banners-card__figure' }, [
      createElement('span', { class: 'banners-card__value' }, [numberFormat(f.value)]),
      createElement('span', { class: 'banners-card__label' }, [f.label]),
    ]));
    el.replaceChildren(createElement('div', { class: 'banners-card__figures' }, figures));
  }

  function load() {
    const mine = ++seq;
    el.setAttribute('aria-busy', 'true');
    ctx.apiClient.get('/banners/cards-stats', { signal: ctx.signal })
      .then((/** @type {unknown} */ raw) => {
        if (destroyed || mine !== seq) return;
        el.setAttribute('aria-busy', 'false');
        render(/** @type {any} */ (raw) || {});
      })
      .catch((/** @type {unknown} */ err) => {
        if (isAbortError(err) || destroyed || mine !== seq) return;
        el.setAttribute('aria-busy', 'false');
        ctx.logger.error('Banners overview load failed', { data: err });
        renderMessage('Couldn’t load overview.');
      });
  }

  renderMessage('Loading…');
  load();

  return {
    el,
    refresh: load,
    destroy() { destroyed = true; },
  };
}
